import {
  AnimatedSprite,
  Assets,
  BitmapText,
  ColorMatrixFilter,
  Container,
  FrameObject,
  Graphics as PixijsGraphics,
  Rectangle,
  SCALE_MODES,
  Texture,
  TextureSource,
} from "pixi.js";
import { SimpleEventDispatcher } from "strongly-typed-events";
import { RenderSystem } from "@/client/renderer";
import { ResourceEvent } from "@/client/communication/api/bindings/ResourceEvent";
import { match, P } from "ts-pattern";
import { ResourceBundle } from "@/client/communication/api/bindings/ResourceBundle";
import { Tileset } from "@/client/communication/api/blueprints/Tileset";
import { LoadResource } from "@/client/communication/api/bindings/LoadResource";
import { GidMap } from "@/editor/blueprints/GidMap";
import { GameNodeKind } from "@/editor/blueprints/GameNodeKind";
import { RENDER_SCALE } from "@/shared/index";
import { Collider } from "@/editor/blueprints/Collider";
import { create_dummy_pic } from "@/client/renderer/create_game_renderer";
import { CharAnimationToTilesetMap } from "@/editor/blueprints/CharAnimationToTilesetMap";
import { GameInstanceMap } from "@/client/game-instance";
import { TextRender } from "@/editor/blueprints/TextRender";
import { TextStyleAlign } from "pixi.js/lib/scene/text/TextStyle";
import { EffectsManager } from "@/client/effects-manager";
import {
  create_fade_in_animation,
  create_fade_out_animation,
} from "@/client/sprite-animations";
import { Howl, Howler } from "howler";
import { EntityLayerManager } from "@/client/entity-layer-manager";
import { NodeContainer } from "@/editor/stores/game-instances";
import { ProgressBar as ProgressBarData } from "@/editor/blueprints/ProgressBar";
import { ProgressBar } from "@pixi/ui";
import { tileset_key } from "@/editor/stores/editor";
import { AudioSet } from "@/editor/blueprints/AudioSet";
import {
  is_sound_on,
  is_window_active,
  is_window_visible,
} from "@/client/game";

export interface Graphics {
  textures: Texture[];
  frame_objects: FrameObject[];
  loop_animation?: boolean | null;
}

export type ResourceManagerMap = { [module_id: string]: ResourceManager };

const SAME_SOUND_DELAY = 50;

export class ResourceManager {
  image_texture_map: {
    [path: string]: Texture;
  } = {};
  sound_map: {
    [path: string]: Howl | "loading";
  } = {};
  sound_played_last_time: { [path: string]: number } = {};
  gid_map_for_terrain: {
    [gid: string]: Graphics;
  } = {};
  tileset_resource_to_graphics_map: {
    [tileset_path: string]: { [id: string]: Graphics };
  } = {};
  gid_map: GidMap = [];
  character_animation_to_tileset_map: CharAnimationToTilesetMap = {};
  tilesets: Tileset[] = [];
  audio_sets: AudioSet[] = [];
  tile_set_map: {
    [path: string]: Tileset;
  } = {};
  audio_set_map: {
    [path: string]: AudioSet;
  } = {};
  dummy_texture_tileset_missing: Texture;
  dummy_texture_loading: Texture;
  resource_bundle_complete = new SimpleEventDispatcher<{
    module_id: string;
    instance_id: string;
    bundle_name: string;
  }>();
  resources_unload = new SimpleEventDispatcher<void>();

  constructor(
    private _base_url: string,
    renderer: RenderSystem,
    public module_id: string,
  ) {
    this.dummy_texture_tileset_missing = renderer.dummy_texture_tileset_missing;
    this.dummy_texture_loading = renderer.dummy_texture_loading;
    this.gid_map_for_terrain["0"] = {
      textures: [Texture.EMPTY],
      frame_objects: [],
    };
  }

  get_tileset_path_and_id_from_gid(gid: number): [string, number] {
    const [tileset, start_gid] = this._get_tileset_by_gid(gid);
    if (!tileset) {
      console.error("No tileset for gid", gid, this.gid_map, this.module_id);
      return ["", 0];
    }
    return [tileset_key(tileset), gid - start_gid];
  }

  set_tileset_map(tilesets: Tileset[]) {
    for (const tileset of tilesets) {
      this.tile_set_map[
        `${tileset.resource_path}/${tileset.name}.tileset.json`
      ] = tileset;
    }
  }

  set_audio_set_map(audio_sets: AudioSet[]) {
    for (const audio_set of audio_sets) {
      this.audio_set_map[
        `${audio_set.resource_path}/${audio_set.name}.audio.json`
      ] = audio_set;
    }
  }

  async add_loading_to_texture_map(path: string) {
    const loading = Texture.from(await create_dummy_pic("#FF00ff"));
    this.image_texture_map[path] = Texture.from(loading.source);
    this.image_texture_map[path].source.scaleMode = SCALE_MODES.NEAREST;
    this.image_texture_map[path].source.update();
  }

  load_sound_from_asset(asset: LoadResource) {
    return new Howl({
      src: [`${this._base_url}/${asset.path}?q=${asset.cache_hash}`],
      preload: true,
    });
  }

  get_tileset_from_char_anim_by_variant(
    char_anim_path: string,
    variant_name: string,
  ): string {
    const variant_map = this.character_animation_to_tileset_map[char_anim_path];
    const resource_path = variant_map[variant_name] ?? variant_map["default"];
    return resource_path ?? "";
  }

  play_sound(
    audio_set_path: string,
    key: string,
    base_volume: number = 1,
    rand_volume: number = 0,
    rand_speed: number = 0,
  ) {
    if (!is_window_active() || !is_window_visible() || !is_sound_on()) {
      return;
    }
    const sound_path = this.audio_set_map[audio_set_path]?.audio_files[key];
    if (!sound_path) {
      console.error("No audio set for", audio_set_path, key);
      return;
    }
    const sound = this.sound_map[sound_path];
    if (sound && sound !== "loading") {
      const last_played = this.sound_played_last_time[sound_path] || 0;
      const time_since_last_play = Date.now() - last_played;
      if (time_since_last_play >= SAME_SOUND_DELAY) {
        const random_vol = (Math.random() * rand_volume) / 100;
        sound.volume(base_volume - random_vol);
        const random = (Math.random() * rand_speed - rand_speed / 2) / 100;
        sound.rate(1 + random);
        sound.play();
        this.sound_played_last_time[sound_path] = Date.now();
      }
    }
  }

  async load_resource_bundle(
    module_id: string,
    instance_id: string,
    resource_bundle: ResourceBundle,
    dispatch_resource_bundle_complete: boolean = false,
  ) {
    const bundle_id = `${module_id}-${resource_bundle.name}`;
    const path_to_resource_map = resource_bundle.assets.reduce(
      (acc, r) => ({ ...acc, [r.path]: r }),
      {} as { [path: string]: LoadResource },
    );
    for (const asset of resource_bundle.assets) {
      await match(asset.kind)
        .with("Image", async () => {
          if (!this.image_texture_map[asset.path]) {
            await this.add_loading_to_texture_map(asset.path);
          }
          return Promise.resolve();
        })
        .with("Font", async () => {
          console.log("font", asset);
          return Promise.resolve();
        })
        .with("Audio", async () => {
          this.sound_map[asset.path] = this.load_sound_from_asset(asset);
          console.log("audio", asset);
          return Promise.resolve();
        })
        .with("Unknown", async () => Promise.resolve())
        .exhaustive();
    }
    try {
      Assets.addBundle(
        bundle_id,
        resource_bundle.assets
          .filter((a) => a.kind !== "Audio")
          .map((asset) => ({
            alias: asset.path,
            src: `${this._base_url}/${asset.path}?q=${asset.cache_hash}`,
          })),
      );
      const r = await Assets.loadBundle(bundle_id);
      for (const load_resource of resource_bundle.assets) {
        const path = load_resource.path;
        const loaded_resource = r[path];
        if (!loaded_resource) {
          console.error(`${path} did not load?!`);
          continue;
        }
        match(path_to_resource_map[path].kind)
          .with("Image", () => {
            this.image_texture_map[path].source.resource = (
              loaded_resource as Texture
            ).source.resource;
            this.image_texture_map[path].source.scaleMode = SCALE_MODES.NEAREST;
            this.image_texture_map[path].source.update();
          })
          .with("Font", async () => {
            console.log(loaded_resource);
          })
          .with("Audio", async () => {
            console.log("Howler should do this!");
          })
          .with("Unknown", () => {})
          .exhaustive();
      }
      if (dispatch_resource_bundle_complete) {
        this.resource_bundle_complete.dispatch({
          module_id,
          instance_id,
          bundle_name: resource_bundle.name,
        });
      }
      this._update_uv_maps();
    } catch (e) {
      console.error("Could not load bundle", e);
    }
  }

  create_bitmap_text(text_render: TextRender) {
    const bitmap_text = new BitmapText({
      text: text_render.text,
      style: {
        fontFamily: text_render.font_family,
        fontSize: text_render.size,
        align: text_render.align.toLowerCase() as TextStyleAlign,
        letterSpacing: text_render.letter_spacing,
      },
    });
    match(text_render.align)
      .with("Center", () => {
        bitmap_text.pivot.set(Math.round(bitmap_text.width / 2), 0);
      })
      .with("Left", () => {
        bitmap_text.pivot.set(0, 0);
      })
      .with("Right", () => {
        bitmap_text.pivot.set(bitmap_text.width, 0);
      })
      .with("Justify", () => {
        bitmap_text.pivot.set(0, 0);
      })
      .exhaustive();
    return bitmap_text;
  }

  private _update_uv_maps() {
    for (const g of Object.values(this.gid_map_for_terrain)) {
      for (const t of g.textures) {
        t.updateUvs();
      }
    }
    for (const graphics_map of Object.values(
      this.tileset_resource_to_graphics_map,
    )) {
      for (const g of Object.values(graphics_map)) {
        for (const t of g.textures) {
          t.updateUvs();
        }
      }
    }
  }

  handle_resource_event(
    resource_event: ResourceEvent,
    game_instance_map: GameInstanceMap,
  ) {
    match(resource_event)
      .with({ LoadResource: P.select() }, (resource_bundle) => {
        resource_bundle.assets
          .filter((a) => a.kind === "Audio")
          .forEach((a) => {
            if (a.path && a.path.trim() === "") {
              console.warn("Could not load empty audio");
              return;
            }
            if (!this.sound_map[a.path]) {
              this.sound_map[a.path] = this.load_sound_from_asset(a);
            }
          });
        Assets.addBundle(
          resource_bundle.name,
          resource_bundle.assets
            .filter((a) => a.kind !== "Audio")
            .map((asset) => ({
              alias: asset.path,
              src: `${this._base_url}/${asset.path}?q=${asset.cache_hash}`,
            })),
        );
        Assets.loadBundle(resource_bundle.name).then((r) => {
          for (const res of resource_bundle.assets) {
            match(res.kind)
              .with("Image", () => {
                if (!r[res.path] || (res.path && res.path.trim() === "")) {
                  console.warn("Could not load empty image");
                  return;
                }
                if (!this.image_texture_map[res.path]) {
                  this.image_texture_map[res.path] = r[res.path] as Texture;
                  this.image_texture_map[res.path].source.scaleMode =
                    SCALE_MODES.NEAREST;
                  console.log("Loaded", res.path);
                } else {
                  try {
                    this.image_texture_map[res.path].source.resource = (
                      r[res.path] as Texture
                    ).source.resource;
                    this.image_texture_map[res.path].source.scaleMode =
                      SCALE_MODES.NEAREST;
                    this.image_texture_map[res.path].source.update();
                  } catch (e) {
                    console.error("Could not update texture", res, e);
                  }
                }
              })
              .with("Audio", () => {
                console.log("Howler should do this!");
              })
              .otherwise((d) => {
                console.error(
                  `Tried to load resource kind that is not handled ${d}`,
                );
              });
          }
          this._update_uv_maps();
        });
      })
      .with({ LoadTileAndAudioSets: P.select() }, ([tile_sets, audio_sets]) => {
        this.set_tileset_map(tile_sets);
        this.set_audio_set_map(audio_sets);
      })
      .with(
        { UpdateGidAndCharAnimMap: P.select() },
        ([gid_map, character_animation_to_tileset_map]) => {
          this.tileset_resource_to_graphics_map = {};
          this.gid_map = gid_map;
          this.character_animation_to_tileset_map =
            character_animation_to_tileset_map;
        },
      )
      .with(
        { UpdateAudioSet: P.select() },
        ([resource_path, audio_set_update]) => {
          match(audio_set_update)
            .with({ SetAudio: P.select() }, ([sound_path, sound]) => {
              if (this.audio_set_map[resource_path]) {
                this.audio_set_map[resource_path].audio_files[sound_path] =
                  sound;
              }
            })
            .with({ RemoveAudio: P.select() }, (sound_path) => {
              if (this.audio_set_map[resource_path]) {
                delete this.audio_set_map[resource_path].audio_files[
                  sound_path
                ];
              }
            })
            .otherwise(() => {});
        },
      )
      .with(
        { UpdateTileset: P.select() },
        ([resource_path, tileset_update]) => {
          match(tileset_update)
            .with({ ChangeTileImage: P.select() }, ([id_in_tileset, image]) => {
              this.tile_set_map[resource_path].tiles[id_in_tileset] = {
                id: id_in_tileset,
                image,
                animation:
                  this.tile_set_map[resource_path].tiles[id_in_tileset]
                    ?.animation || [],
                loop_animation:
                  this.tile_set_map[resource_path].tiles[id_in_tileset]
                    ?.loop_animation || null,
                collision_shape:
                  this.tile_set_map[resource_path].tiles[id_in_tileset]
                    ?.collision_shape || null,
              };

              this._invalidate_graphics_cache_for_id_in_tileset(
                id_in_tileset,
                resource_path,
              );
            })
            .with({ AddTile: P.select() }, ([id_in_tileset, tile]) => {
              this.tile_set_map[resource_path].tiles[id_in_tileset] = tile;
              this._invalidate_graphics_cache_for_id_in_tileset(
                id_in_tileset,
                resource_path,
              );
            })
            .with({ RemoveTile: P.select() }, (id_in_tileset) => {
              delete this.tile_set_map[resource_path].tiles[id_in_tileset];
              this._invalidate_graphics_cache_for_id_in_tileset(
                id_in_tileset,
                resource_path,
              );
            })
            .with(
              { SetTileLooping: P.select() },
              ([id_in_tileset, loop_animation]) => {
                if (
                  this.tile_set_map[resource_path] &&
                  this.tile_set_map[resource_path].tiles[id_in_tileset]
                ) {
                  this.tile_set_map[resource_path].tiles[
                    id_in_tileset
                  ].loop_animation = loop_animation;
                }
              },
            )
            .with(
              { ChangeTileAnimation: P.select() },
              ([id_in_tileset, simple_animation_frames]) => {
                if (
                  simple_animation_frames !== null &&
                  this.tile_set_map[resource_path]
                ) {
                  if (!this.tile_set_map[resource_path].tiles[id_in_tileset]) {
                    this.tile_set_map[resource_path].tiles[id_in_tileset] = {
                      id: id_in_tileset,
                      animation: [],
                      loop_animation: null,
                      image: null,
                      collision_shape: null,
                    };
                  }
                  this.tile_set_map[resource_path].tiles[
                    id_in_tileset
                  ].animation = simple_animation_frames;
                }

                if (
                  this.tile_set_map[resource_path] &&
                  this.tile_set_map[resource_path].tiles[id_in_tileset] &&
                  simple_animation_frames
                ) {
                  this.tile_set_map[resource_path].tiles[
                    id_in_tileset
                  ].animation = simple_animation_frames;
                  this._invalidate_graphics_cache_for_id_in_tileset(
                    id_in_tileset,
                    resource_path,
                  );
                  for (const worlds of Object.values(game_instance_map)) {
                    for (const game_instance of Object.values(worlds)) {
                      game_instance.update_sprite_animations(
                        this,
                        resource_path,
                        id_in_tileset,
                      );
                    }
                  }
                }
              },
            )
            .otherwise(() => {});
        },
      )
      .with("UnLoadResources", () => console.log("unload"))
      .exhaustive();
  }

  private _invalidate_graphics_cache_for_id_in_tileset(
    id_in_tileset: number,
    resource_path: string,
  ) {
    delete this.gid_map_for_terrain[
      this.get_gid_from_local_id(id_in_tileset, resource_path)
    ];
    const graphics_map = this.tileset_resource_to_graphics_map[resource_path];
    if (graphics_map) {
      delete graphics_map[id_in_tileset];
    }
  }

  get_sprite_from_graphics(graphics: Graphics): AnimatedSprite {
    const sprite = new AnimatedSprite(
      graphics.frame_objects.length > 0
        ? graphics.frame_objects
        : graphics.textures,
    );
    sprite.loop = graphics.loop_animation !== false;
    sprite.eventMode = "static";
    sprite.cursor = "pointer";
    sprite.anchor.set(0.5, 0.5);
    return sprite;
  }

  get_gid_from_local_id(id_in_tileset: number, tileset_path: string): number {
    const tileset = this.tile_set_map[tileset_path];
    if (!tileset) {
      console.error("No tileset for", tileset_path, this.module_id);
      return 0;
    }
    const start_gid = this.gid_map.find((g) => g[0] === tileset_path)?.[1] || 0;
    return id_in_tileset + start_gid;
  }

  get_graphics_by_id_and_tileset_path(
    id_in_tileset: number,
    tileset_path: string,
  ): Graphics {
    const tileset = this.tile_set_map[tileset_path];
    if (!tileset) {
      console.error("No tileset for", tileset_path, this.module_id);
      return {
        textures: [this.dummy_texture_tileset_missing],
        frame_objects: [],
      };
    }
    if (
      (tileset.image && id_in_tileset >= tileset.tile_count) ||
      (!tileset.image && id_in_tileset >= Object.keys(tileset.tiles).length)
    ) {
      return this._calculate_graphics(id_in_tileset, tileset);
    }
    if (!this.tileset_resource_to_graphics_map[tileset_path]) {
      this.tileset_resource_to_graphics_map[tileset_path] = {};
    }
    if (!this.tileset_resource_to_graphics_map[tileset_path]?.[id_in_tileset]) {
      this.tileset_resource_to_graphics_map[tileset_path][id_in_tileset] =
        this._calculate_graphics(id_in_tileset, tileset);
    }

    return this.tileset_resource_to_graphics_map[tileset_path][id_in_tileset];
  }

  private _get_tileset_by_gid(gid: number): [Tileset | undefined, number] {
    let selected_gid_index = 0;
    for (let i = 0; i < this.gid_map.length; i++) {
      const start_gid = this.gid_map[i][1];
      if (gid < start_gid) {
        break;
      }
      selected_gid_index = i;
    }
    if (!this.gid_map[selected_gid_index]) {
      console.error("No tileset for gid", gid, this.gid_map, this.module_id);
      return [undefined, 0];
    }
    const [path, start_gid] = this.gid_map[selected_gid_index];
    return [this.tile_set_map[path], start_gid];
  }

  private _calculate_graphics(
    id: number,
    tileset: Tileset | undefined,
  ): Graphics {
    const graphics: Graphics = { textures: [], frame_objects: [] };
    if (id < 0 || !tileset) {
      graphics.textures.push(this.dummy_texture_tileset_missing);
      return graphics;
    }

    ///const animation_frames = tileset.tile_animation_map[id];

    if (tileset.image) {
      const texture_source: TextureSource | undefined =
        this.image_texture_map[tileset.image.path]?.source;
      if (!texture_source) {
        graphics.textures.push(this.dummy_texture_loading);
        console.error(
          "No base_texture even though there should be a dummy at the very least!",
        );
        return graphics;
      }
      const tile = tileset.tiles[id];
      graphics.loop_animation = tile?.loop_animation ?? null;
      const animation_frames = tile?.animation ?? [];
      if (animation_frames.length === 0) {
        const x = ((id - 1) % tileset.columns) * tileset.tile_width;
        const y = Math.floor((id - 1) / tileset.columns) * tileset.tile_height;
        const texture = new Texture({
          source: texture_source,
          frame: new Rectangle(x, y, tileset.tile_width, tileset.tile_height),
        });
        graphics.textures.push(texture);
      } else {
        for (const frame of animation_frames) {
          const x = ((frame.id - 1) % tileset.columns) * tileset.tile_width;
          const y =
            Math.floor((frame.id - 1) / tileset.columns) * tileset.tile_height;
          const texture = new Texture({
            source: texture_source,
            frame: new Rectangle(x, y, tileset.tile_width, tileset.tile_height),
          });
          graphics.frame_objects.push({ texture, time: frame.duration });
        }
      }
    } else {
      const tile = tileset.tiles[id];
      graphics.loop_animation = tile?.loop_animation ?? null;
      const image_path = tile?.image?.path;
      if (!image_path) {
        graphics.textures.push(this.dummy_texture_loading);
        console.error("Could not find image path for tile!?");
        return graphics;
      }
      const texture_source: TextureSource | undefined =
        this.image_texture_map[image_path]?.source;
      if (!texture_source) {
        graphics.textures.push(this.dummy_texture_loading);
        console.error("Could not find image source for tile!?");
        return graphics;
      }
      const texture = new Texture({
        source: texture_source,
      });
      graphics.textures.push(texture);
    }

    return graphics;
  }
}

export function create_progress_bar(
  resource_manager: ResourceManager,
  progress_bar: ProgressBarData,
) {
  const bg = resource_manager.get_sprite_from_graphics(
    resource_manager.get_graphics_by_id_and_tileset_path(
      progress_bar.background,
      progress_bar.tileset,
    ),
  );
  const fill = resource_manager.get_sprite_from_graphics(
    resource_manager.get_graphics_by_id_and_tileset_path(
      progress_bar.fill,
      progress_bar.tileset,
    ),
  );
  bg.anchor.set(0, 0);
  fill.anchor.set(0, 0);
  const pg = new ProgressBar({
    bg,
    fill,
    progress: progress_bar.progress,
  });
  pg.width = progress_bar.width;
  pg.height = progress_bar.height;
  return pg;
}

export function create_color_matrix_filter() {
  return new ColorMatrixFilter();
}

export function create_display_object(
  node: GameNodeKind,
  resource_manager: ResourceManager,
  effects_manager: EffectsManager,
  entity_layer_manager: EntityLayerManager,
  show_colliders: boolean = false,
  instance_node_id?: string,
): NodeContainer {
  const container = new Container();
  match(node)
    .with({ Node2D: P.select() }, (game_node) => {
      container.x = game_node.data.transform.position[0] * RENDER_SCALE;
      container.y = game_node.data.transform.position[1] * RENDER_SCALE;
      container.rotation = game_node.data.transform.rotation;
      container.scale.set(
        game_node.data.transform.scale[0],
        game_node.data.transform.scale[1],
      );

      match(game_node.data.kind)
        .with({ Node2D: P.select() }, { Instance: P.select() }, () => {
          //container.addChild(new Text(game_node.name, { fill: "white" }));
        })
        .with({ Render: P.select() }, (render) => {
          const display_object = match(render.kind)
            .with(
              { Sprite: P.select() },
              ({
                gid: id_in_tileset,
                resource_path: tileset_path,
                color,
                fade_in_effect,
                fade_out_effect,
              }) => {
                const sprite = resource_manager.get_sprite_from_graphics(
                  resource_manager.get_graphics_by_id_and_tileset_path(
                    id_in_tileset,
                    tileset_path,
                  ),
                );

                effects_manager.add_sprite_with_effects(
                  sprite,
                  game_node.entity_id ? `${game_node.entity_id}` : game_node.id,
                  tileset_path,
                  id_in_tileset,
                  create_fade_in_animation(
                    fade_in_effect[0],
                    fade_in_effect[1],
                  ),
                  create_fade_out_animation(
                    fade_out_effect[0],
                    fade_out_effect[1],
                  ),
                  color,
                );
                return sprite;
              },
            )
            .with(
              { AnimatedSprite: P.select() },
              ({
                gid: id_in_tileset,
                char_anim_path,
                tileset_variant_name,
                fade_in_effect,
                fade_out_effect,
                color,
              }) => {
                const variant_map =
                  resource_manager.character_animation_to_tileset_map[
                    char_anim_path
                  ];
                const tileset_path =
                  variant_map[tileset_variant_name] ?? variant_map["default"];
                const sprite = resource_manager.get_sprite_from_graphics(
                  resource_manager.get_graphics_by_id_and_tileset_path(
                    id_in_tileset,
                    tileset_path,
                  ),
                );
                effects_manager.add_sprite_with_effects(
                  sprite,
                  game_node.entity_id ? `${game_node.entity_id}` : game_node.id,
                  tileset_path,
                  id_in_tileset,
                  create_fade_in_animation(
                    fade_in_effect[0],
                    fade_in_effect[1],
                  ),
                  create_fade_out_animation(
                    fade_out_effect[0],
                    fade_out_effect[1],
                  ),
                  color,
                );
                return sprite;
              },
            )
            .with({ Text: P.select() }, (text_render) =>
              resource_manager.create_bitmap_text(text_render),
            )
            .with({ ProgressBar: P.select() }, (progress_bar_data) =>
              create_progress_bar(resource_manager, progress_bar_data),
            )
            .exhaustive();
          entity_layer_manager.add_display_object(
            game_node,
            render.layer,
            display_object,
            window.medium.create_container,
            instance_node_id,
          );
        })
        .with({ RigidBody: P.select() }, (_) => {})
        .with({ Collider: P.select() }, (collider) => {
          const [graphics, pivot_x, pivot_y] =
            create_collider_graphic(collider);
          container.addChild(graphics);
          container.pivot.x = pivot_x * RENDER_SCALE;
          container.pivot.y = pivot_y * RENDER_SCALE;
          graphics.visible = show_colliders;
          entity_layer_manager.add_display_object(
            game_node,
            "FG10",
            graphics,
            window.medium.create_container,
            instance_node_id,
          );
        })
        .exhaustive();
    })
    .exhaustive();

  if (container.children.length > 0 && node.Node2D) {
    container.children[0].scale.set(
      node.Node2D.data.transform.scale[0],
      node.Node2D.data.transform.scale[1],
    );
  }
  const node_container = container as NodeContainer;
  node_container.game_node_id = node.Node2D.id;
  node_container.entity_id = node.Node2D.entity_id;
  node_container.instance_node_id = instance_node_id;
  return node_container;
}

export function create_collider_graphic(
  collider: Collider,
): [PixijsGraphics, number, number] {
  return match(collider.shape)
    .with({ Ball: P.select() }, (radius): [PixijsGraphics, number, number] => {
      const graphics = new PixijsGraphics()
        .circle(0, 0, radius * RENDER_SCALE)
        .stroke({
          color: "#ff0000",
          width: 1,
        });
      return [graphics, 0, 0];
    })
    .with(
      { CapsuleX: P.select() },
      ([_half_y, _radius]): [PixijsGraphics, number, number] => {
        const graphics = new PixijsGraphics()
          .circle(0, 0, RENDER_SCALE)
          .stroke({
            color: "#ff0000",
            width: 1,
          });
        return [graphics, 1, 1];
      },
    )
    .with(
      { CapsuleY: P.select() },
      ([_half_x, _radius]): [PixijsGraphics, number, number] => {
        const graphics = new PixijsGraphics()
          .circle(0, 0, RENDER_SCALE)
          .stroke({
            color: "#ff0000",
            width: 1,
          });
        return [graphics, 1, 1];
      },
    )
    .with(
      { Cuboid: P.select() },
      ([a, b]): [PixijsGraphics, number, number] => {
        const graphics = new PixijsGraphics()
          .rect(
            -a * RENDER_SCALE,
            -b * RENDER_SCALE,
            a * 2 * RENDER_SCALE,
            b * 2 * RENDER_SCALE,
          )
          .stroke({
            color: "#ff0000",
            width: 1,
          });
        return [graphics, 0, 0];
      },
    )
    .exhaustive();
}

/*
      for (const frame of animation_frames) {
        const x = (frame.tile_id % tileset.columns) * tileset.width;
        const y = Math.floor(frame.tile_id / tileset.columns) * tileset.height;
        const texture = new Texture(
          tileset.tile_set_image_resource.texture.baseTexture,
          new Rectangle(x, y, tileset.width, tileset.height),
        );

        graphics.textures.push(texture);
        graphics.frame_objects.push({ texture, time: frame.duration });
      } */
